import { FormGroup, Validators } from "@angular/forms";
import { FleetPermissionTableComponent } from "@app/features/admin/modules/dictionary-modules/fleet-permission-table/fleet-permission-table.component";
import {
  IFleet,
  IFleetParams,
  IFleetPermissionSave,
  IFleetSave,
} from "@app/shared/data-access/src/lib/resource-management/models/fleet";
import { ResourceManagementEndpoints } from "@app/shared/data-access/src/lib/resource-management/types/resource-management-endpoints";
import {
  HardFormFieldTypeEnum,
  HardTableColumnTypeEnum,
  IHardFormField,
  IHardFormFieldFilter,
  IHardFormSelectParams,
  IHardTable,
  IHardFormComponentParams,
} from "@app/shared/ui-lib/src/lib";
import { IBaseConfig } from "@models/form/base-config";
import { LangEnum } from "@models/interface-management/localization";
import { TranslationFieldPartialValidator } from "@static/fields/fields-constant";

export class FleetConfig implements IBaseConfig {
  version = 1;
  dicType = ResourceManagementEndpoints.Fleet;

  fields: IHardFormField[] = [
    {
      name: "name",
      params: {
        direction: "row",
        fields: TranslationFieldPartialValidator(
          "dicTable.name",
          HardFormFieldTypeEnum.Input,
          [LangEnum.RU, LangEnum.KZ, LangEnum.EN],
        ),
      },
    },
    {
      title: "dicTable.fleetOwnershipType",
      name: "fleetOwnershipType",
      type: HardFormFieldTypeEnum.Dictionary,
      params: {
        dicType: "api/v1/" + ResourceManagementEndpoints.FleetOwnershipType,
        validators: [Validators.required],
      } as IHardFormSelectParams,
    },
    {
      title: "dicTable.businessPartner",
      name: "businessPartner",
      type: HardFormFieldTypeEnum.Dictionary,
      params: {
        dicType: "api/v1/" + ResourceManagementEndpoints.BusinessPartner,
        conditionalFields: ["fleetOwnershipType"],
        conditionalCallback: (
          _data: IFleet,
          config: IHardFormField,
          form: FormGroup,
        ) => {
          const code = form
            .get("fleetOwnershipType")
            ?.value?.code?.toString()
            .toLowerCase();
          const shouldShow = !(!code || code === "internal");
          config.params.hide = !shouldShow;
          const ctrl = form.get("businessPartner");
          if (ctrl) {
            if (shouldShow) {
              ctrl.setValidators([Validators.required]);
            } else {
              ctrl.clearValidators();
            }
            ctrl.updateValueAndValidity({ emitEvent: false });
          }
        },
      } as IHardFormSelectParams,
    },
    {
      type: HardFormFieldTypeEnum.Component,
      name: "permissions",
      params: {
        component: FleetPermissionTableComponent,
      } as IHardFormComponentParams,
    },
  ];

  saveCallback(data: IFleet): IFleetSave {
    const isExternal =
      data?.fleetOwnershipType?.code?.toString()?.toLowerCase() !== "internal";

    return {
      id: data?.id,
      name: data.name,
      fleetOwnershipTypeId: data?.fleetOwnershipType?.id,
      businessPartnerId: isExternal ? (data?.businessPartner?.id ?? null) : null,
      aadGroupId: data?.aadGroupId ?? null,
      permissions: (data?.permissions as IFleetPermissionSave[]) ?? [],
    };
  }

  filterTemplateColumns = "1fr 250px";
  filterFields: IHardFormFieldFilter<IHardFormSelectParams>[] = [
    {
      name: "searchTerm",
      type: HardFormFieldTypeEnum.Input,
      placeholder: "base.search",
      outside: true,
    },
    {
      name: "fleetOwnershipType",
      type: HardFormFieldTypeEnum.Dictionary,
      placeholder: "dicTable.allfleetOwnershipTypes",
      outside: true,
      params: {
        dicType: "api/v1/" + ResourceManagementEndpoints.FleetOwnershipType,
      },
    },
  ];

  filterCallback(data: IFleetParams): IFleetParams {
    return {
      searchTerm: data?.searchTerm ?? "",
      fleetOwnershipTypeId: data?.fleetOwnershipType?.id ?? null,
    };
  }

  tableConfig: IHardTable<IFleet> = {
    showDefaultActions: true,
    tableColumns: "44px repeat(6,auto)",
    columns: [
      {
        title: "№",
        name: "index",
        type: HardTableColumnTypeEnum.Index,
      },
      {
        title: "dicTable.name",
        name: "name",
        type: HardTableColumnTypeEnum.Translate,
      },
      {
        title: "dicTable.fleetOwnershipType",
        name: "name",
        type: HardTableColumnTypeEnum.Parent,
        parent: "fleetOwnershipType",
      },
      {
        title: "dicTable.businessPartner",
        name: "name",
        type: HardTableColumnTypeEnum.Parent,
        parent: "businessPartner",
      },
      {
        title: "dicTable.equipmentCount",
        name: "equipmentCount",
        type: HardTableColumnTypeEnum.Number,
      },
      {
        title: "dicTable.permissionsCount",
        name: "permissionsCount",
        type: HardTableColumnTypeEnum.Number,
      },
    ],
  };
}
